"use client";

import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'next/navigation';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useLanguage } from '@/context/LanguageContext';
import { jobCategories } from '@/data/categories';
import styles from './Footer.module.css';
import ProtectedEmail from './ProtectedEmail';

const defaultFooterCategories = [
  "Administrim",
  "Art dhe Televizion",
  "Biznes-Menaxhim Strategjik",
  "Burime Njerëzore",
  "Financë",
  "HOREKA-Hoteleri-Restorant-Kafe",
  "Instalime-Mirëmbajtje",
  "Ligjore",
  "Marketing-Produkte",
  "Mjekësore-Shëndetësore",
  "Ndërtim dhe Industri e rëndë",
  "Profesionistët",
  "Prokurim dhe Logjistikë",
  "Shërbim Klienti",
  "Shitje-Zhvillim Biznesi",
  "Siguria Kompjuterike, Cilësisë, Ushqimore dhe Fizike",
  "Sport",
  "Teknologji Informacioni"
];

export default function Footer() {
  const params = useParams();
  const lang = params?.lang || 'en';
  const { language, setLanguage, t } = useLanguage();
  const { status } = useSession();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [categories, setCategories] = useState<{ name: string; query: string }[]>([]);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsLoggedIn(localStorage.getItem('isLoggedIn') === 'true' || status === 'authenticated');
  }, [status]);

  useEffect(() => {
    async function loadCategories() {
      try {
        const cachedTree = localStorage.getItem(`vacancy_categories_v2_${language}`);
        if (cachedTree) {
          const parsed = JSON.parse(cachedTree);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setCategories(parsed.map((item: any) => ({
              name: item.value || item.name,
              query: item.value || item.name
            })));
            return;
          }
        }

        const res = await fetch('/api/categories');
        let allData: any[] = [];
        if (res.ok) {
          allData = await res.json();
        }

        if (allData.length > 0) {
          const cleanCategory = (html: string) => {
            let text = html || '';
            text = text.replace(/<[^>]+>/g, '');
            text = text.replace(/&amp;/g, '&').replace(/&#9744;/g, '');
            text = text.replace(/[☐☑☒]/g, '');
            text = text.replace(/[!.,;:]+$/g, '');
            text = text.replace(/^[!.,;:]+/g, '');
            return text.trim();
          };

          const getCatName = (c: any) => {
            let catName = cleanCategory(c.name);
            if (language === 'en' && c.acf?.name_en) {
              catName = cleanCategory(c.acf.name_en);
            }
            return catName;
          };

          const parents = allData
            .filter((c: any) => !c.parent || c.parent === 0)
            .sort((a: any, b: any) => getCatName(a).localeCompare(getCatName(b)))
            .map((c: any) => {
              const name = getCatName(c);
              return {
                name,
                query: name
              };
            });

          if (parents.length > 0) {
            setCategories(parents);
            return;
          }
        }
      } catch (e) {
        // Fallback to defaults cleanly
      }

      // Fallback to defaults
      setCategories(defaultFooterCategories.map(cat => ({
        name: t(cat),
        query: cat
      })));
    }

    loadCategories();
  }, [language, t]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayList = categories.length > 0 
    ? categories 
    : defaultFooterCategories.map(cat => ({ name: t(cat), query: cat }));

  const perCol = Math.ceil(displayList.length / 3);
  const col1 = displayList.slice(0, perCol);
  const col2 = displayList.slice(perCol, perCol * 2);
  const col3 = displayList.slice(perCol * 2);

  return (
    <footer className={styles.footer}>
      <div className="container">
        <div className={styles.topSection}>
          <div className={styles.navAndHelpGroup}>
            <div className={styles.column}>
              <h3 className={styles.columnTitle}>{t('Navigation')}</h3>
              <ul className={styles.linkList}>
                <li><Link href={`/${lang}/vacancies`}>{t('Vacancies')}</Link></li>
                <li><Link href={`/${lang}/companies`}>{t('Companies')}</Link></li>
                <li><Link href={`/${lang}/create-cv`}>{t('Generate CV')}</Link></li>
                <li><Link href={`/${lang}/profile`}>{t('Profile')}</Link></li>
                {!isLoggedIn && (
                  <>
                    <li><Link href={`/${lang}/register?type=company`} rel="nofollow">{t('Company registration')}</Link></li>
                    <li><Link href={`/${lang}/register`}>{t('Create profile')}</Link></li>
                  </>
                )}
                <li><Link href={`/${lang}/profile`} rel="nofollow">{t('Edit info')}</Link></li>
              </ul>
            </div>

            <div className={styles.column}>
              <h3 className={styles.columnTitle}>{t('Help')}</h3>
              <ul className={styles.linkList}>
                <li><Link href="#">{t('Frequently asked questions')}</Link></li>
                <li><ProtectedEmail email="info@myjobs.al" /></li>
                <li><Link href={`/${lang}/feedback`}>{t('Anonymous feedback')}</Link></li>
                <li style={{ marginTop: '0.75rem', display: 'flex', gap: '0.85rem', alignItems: 'center', color: '#6b7280' }}>
                  <a href="https://www.linkedin.com/company/myjobs-al" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="MyJobs on LinkedIn" style={{ display: 'inline-flex', color: 'inherit' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 8.76a1.67 1.67 0 1 0-.01-3.34 1.67 1.67 0 0 0 .01 3.34M7.86 18.5V10.13H5.06V18.5h2.8z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/myjobs.al" target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="MyJobs on Instagram" style={{ display: 'inline-flex', color: 'inherit' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="https://www.facebook.com/myjobs.al" target="_blank" rel="noopener noreferrer" aria-label="Facebook" title="MyJobs on Facebook" style={{ display: 'inline-flex', color: 'inherit' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className={styles.categoriesWrapper}>
            <h3 className={styles.columnTitle}>{t('Categories')}</h3>
            <div className={styles.categoriesGrid}>
              <ul className={styles.linkList}>
                {col1.map((cat, idx) => (
                  <li key={`col1-${idx}`}>
                    <Link href={`/${lang}/vacancies?category=${encodeURIComponent(cat.query)}`} rel="nofollow">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <ul className={styles.linkList}>
                {col2.map((cat, idx) => (
                  <li key={`col2-${idx}`}>
                    <Link href={`/${lang}/vacancies?category=${encodeURIComponent(cat.query)}`} rel="nofollow">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <ul className={styles.linkList}>
                {col3.map((cat, idx) => (
                  <li key={`col3-${idx}`}>
                    <Link href={`/${lang}/vacancies?category=${encodeURIComponent(cat.query)}`} rel="nofollow">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.bottomSectionWrapper}>
        <div className="container">
          <div className={styles.bottomSection}>
            <div className={styles.bottomLinks}>
              <Link href={`/${lang}/terms`}>{t('Terms & Conditions') || 'Terms'}</Link>
              <Link href={`/${lang}/data-processing`}>{t('Data Processing & Privacy') || 'Privacy Policy'}</Link>
            </div>

            <div className={styles.langToggleWrapper} style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '2px' }}>
              <button
                onClick={() => setLanguage('en')}
                style={{ 
                  padding: '4px 12px', 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  border: 'none', 
                  borderRadius: '6px', 
                  backgroundColor: language === 'en' ? '#0b5cd5' : 'transparent', 
                  color: language === 'en' ? 'white' : '#6b7280', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s' 
                }}
              >EN</button>
              <button
                onClick={() => setLanguage('sq')}
                style={{ 
                  padding: '4px 12px', 
                  fontSize: '0.85rem', 
                  fontWeight: 600, 
                  border: 'none', 
                  borderRadius: '6px', 
                  backgroundImage: language === 'sq' ? 'linear-gradient(rgba(243, 244, 246, 0), rgba(243, 244, 246, 0)), url(/alb.jpg)' : 'linear-gradient(rgba(243, 244, 246, 1), rgba(243, 244, 246, 1)), url(/alb.jpg)', 
                  backgroundSize: 'cover', 
                  backgroundPosition: 'center', 
                  color: language === 'sq' ? 'white' : '#6b7280', 
                  cursor: 'pointer', 
                  transition: 'all 0.2s' 
                }}
              >SQ</button>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.tnetSection}>
          <Link href={`/${lang}`} className={styles.logo}>
            <img src="/logo.png?v=2" alt="myjobs.al" style={{ height: '37px', width: 'auto' }} />
          </Link>
          <p className={styles.copyright}>© 2026 {t('All rights reserved')}</p>
        </div>
      </div>
    </footer>
  );
}
